import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Role } from '../../../common/src/model.ts';
import { Pages } from '../../../common/src/pages.ts';
import * as server_api from '../../../common/src/server_api.ts';
import { LocalStorageKey } from '../data/client_local_storage.ts';
import { synchronizeOrgsWithServer, useCurrentOrg } from '../data/use_current_org.tsx';
import { synchronizeHouseholdWithServer, useHousehold } from '../data/use_household.tsx';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { useEffectOnce } from '../util/use_effect_once.ts';
import { useOnline } from '../util/use_online.tsx';
import { Button } from './onboarding/button.tsx';
import {
  ChurchAdminTokenQueryString,
  ChurchIdTokenQueryString,
  finishOnboarding,
  FromChooseUserQueryString,
  goToNextOnboardingStep,
  Message,
  OrgAdminTokenQueryString,
  OrgIdTokenQueryString,
  RoleTokenQueryString,
  setHouseholdOriginBasedOnOrgType,
} from './onboarding/common.tsx';
import { Frame } from './onboarding/frame.tsx';

// URL format: /admin-invite?church-id=5&church-admin-token=11c920f1-180c-109d-ba7e-2d51ee7fe061

export const AdminInvite = () => {
  const { household } = useHousehold();
  const householdToken = household?.token;
  const [activeUserToken] = useLocalStorage(LocalStorageKey.ActiveUserToken);
  const { currentOrg, setHouseholdOrg } = useCurrentOrg();
  const [finished, setFinished] = useState(false);
  const [error, setError] = useState('');
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const orgId = urlParams.get(OrgIdTokenQueryString) ?? urlParams.get(ChurchIdTokenQueryString);
  const orgAdminToken = urlParams.get(OrgAdminTokenQueryString) ?? urlParams.get(ChurchAdminTokenQueryString);
  const role = (urlParams.get(RoleTokenQueryString) ?? Role.Administrator) as Role;
  const navigate = useNavigate();
  const fromChooseUser = urlParams.has(FromChooseUserQueryString);
  const isOnline = useOnline();

  useEffectOnce(() => {
    void (async () => {
      try {
        if (!orgId || !orgAdminToken) {
          setError('Invitation link is invalid.');
          return;
        }

        if (!isOnline) {
          setError('Please check your Internet connection and click the invitation again.');
          return;
        }

        const org = await server_api.getOrg(Number(orgId));
        if (!org) {
          setError(
            'Unable to retrieve organization info from server.  ' +
              'Please check your Internet connection and click the invitation again.',
          );
          return;
        }

        if (!householdToken) {
          setHouseholdOriginBasedOnOrgType(org.type); // to avoid asking later
          await goToNextOnboardingStep(navigate, { from: Pages.AdminInvite, action: 'noHousehold' });
          return;
        }

        if (!fromChooseUser || !activeUserToken) {
          await goToNextOnboardingStep(navigate, { from: Pages.AdminInvite, action: 'chooseUser' });
          return;
        }

        await setHouseholdOrg(org);
        await server_api.setPermissions({ orgAdminToken, userToken: activeUserToken, role });
        await synchronizeOrgsWithServer();
        await synchronizeHouseholdWithServer({ force: true });
        // the invite is where this onboarding ends, even if the visitor never clicks CONTINUE
        await finishOnboarding();
        setFinished(true);
      } catch (e: any) {
        setError(e.message);
      }
    })();
  });

  const onContinue = useCallback(async () => {
    await goToNextOnboardingStep(navigate, { from: Pages.AdminInvite, action: 'continue' });
  }, [navigate]);

  if (!orgId || !orgAdminToken) {
    const href = 'mailto:support@crescendosw.com?subject=Missing church tokens for admin invite';
    return (
      <Frame
        onboardingPage={undefined}
        mainContent={
          <Message $widthInChars={20}>
            An internal error has occurred. <MailLink href={href}>Click here</MailLink> to reach out to support.
          </Message>
        }
        footerContent={<Button onClick={onContinue}>CONTINUE</Button>}
        onEnterKeypress={onContinue}
      />
    );
  }

  if (error) {
    return (
      <Frame
        onboardingPage={undefined}
        mainContent={<Message $widthInChars={22}>{error}</Message>}
        footerContent={<Button onClick={onContinue}>CONTINUE</Button>}
        onEnterKeypress={onContinue}
      />
    );
  }

  if (!finished || !currentOrg) {
    return (
      <Frame
        onboardingPage={undefined}
        mainContent={
          <Message $widthInChars={24}>
            Setting up {role === Role.Administrator ? 'administrator' : 'staff'} account...
          </Message>
        }
        footerContent={<div />}
      />
    );
  }

  return (
    <Frame
      onboardingPage={undefined}
      mainContent={
        <Message $widthInChars={24}>
          Congratulations!
          <br />
          <br />
          As {role === Role.Administrator ? 'an administrator' : 'a staff member'} of{' '}
          {currentOrg.name.length <= 26 ? (
            <span>
              <br />
              {currentOrg.name},<br />
            </span>
          ) : (
            currentOrg.name + ','
          )}{' '}
          your household has been granted full access to the hymnal library.
        </Message>
      }
      footerContent={<Button onClick={onContinue}>CONTINUE</Button>}
      onEnterKeypress={onContinue}
      suppressBackButton={true}
    />
  );
};

const MailLink = styled.a`
  color: blue;
  text-decoration: underline;
`;
